/* eslint-disable react-hooks/immutability -- apiCall is a self-recursive useCallback (used for retry-after-refresh and transient-error retries); the self-reference is intentional. */
/**
 * useApi Hook
 *
 * Provides HTTP API client functionality with:
 * - Automatic Cognito JWT token injection
 * - Token refresh via Cognito SDK
 * - Error handling and retry logic
 * - Support for JSON, blob, and text responses
 */
import { useCallback, useMemo } from 'react';

import { useShadowStore, withExampleGroup } from '@splitfiesta/core';
import {
  RefreshTokenInvalidError,
  getTokens,
  refreshSession,
  refreshTokensWithEndpoint,
} from '@splitfiesta/platform-web/auth/cognito';
import type { SuccessResponseDto } from '@splitfiesta/shared';

import { checkIsOnline } from '@/hooks/useNetworkStatus';
import { useRouter } from '@/hooks/useRouteChangeRouter';

import { APP_VERSION } from '@/lib/appVersion';
import { exampleGroupProvider } from '@/lib/exampleGroup';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/queryClient';

import { useAuthStore } from '@/stores/authStore';

export interface ApiOptions extends RequestInit {
  skipAuth?: boolean;
  onUnauthorized?: () => void;
  responseType?: 'json' | 'blob' | 'text';
  timeoutMs?: number;
}

// Generic API response type
export type ApiResponse<T> = SuccessResponseDto<T> | T;

export interface ApiError extends Error {
  status?: number;
  code?: string;
  details?: unknown;
  translationKey?: string;
}

export interface UseApiReturn {
  apiCall: <TResponse>(
    endpoint: string,
    options?: ApiOptions,
    retryCount?: number
  ) => Promise<ApiResponse<TResponse>>;
  get: <TResponse>(
    endpoint: string,
    options?: ApiOptions
  ) => Promise<ApiResponse<TResponse>>;
  post: <TResponse, TRequest = unknown>(
    endpoint: string,
    body?: TRequest,
    options?: ApiOptions
  ) => Promise<ApiResponse<TResponse>>;
  put: <TResponse, TRequest = unknown>(
    endpoint: string,
    body?: TRequest,
    options?: ApiOptions
  ) => Promise<ApiResponse<TResponse>>;
  patch: <TResponse, TRequest = unknown>(
    endpoint: string,
    body?: TRequest,
    options?: ApiOptions
  ) => Promise<ApiResponse<TResponse>>;
  delete: <TResponse, TRequest = unknown>(
    endpoint: string,
    body?: TRequest,
    options?: ApiOptions
  ) => Promise<ApiResponse<TResponse>>;
  request: <TResponse>(
    endpoint: string,
    options?: ApiOptions,
    retryCount?: number
  ) => Promise<ApiResponse<TResponse>>;
}

const DEFAULT_TIMEOUT_MS = 15000;

// Global cache for refresh promise to prevent concurrent refresh attempts
let globalRefreshPromise: Promise<string | null> | null = null;

export function useApi(): UseApiReturn {
  const setIdToken = useAuthStore(state => state.setIdToken);
  const refreshToken = useAuthStore(state => state.refreshToken);
  const logout = useAuthStore(state => state.logout);
  const router = useRouter();

  const handleAuthError = useCallback(
    (onUnauthorized?: () => void) => {
      // A custom onUnauthorized means the caller owns the 401 — e.g. the
      // PUBLIC deletion-confirm page, where a logged-in visitor must not be
      // silently logged out (or have their cache wiped) by an unrelated
      // response. Only the default path tears the session down.
      if (onUnauthorized) {
        onUnauthorized();
        return;
      }

      // Clear all React Query cache to prevent data leakage between users
      queryClient.clear();
      // Clear auth state
      logout();
      router.push('/login');
    },
    [logout, router]
  );

  /**
   * Refresh the ID token using Cognito SDK or OAuth endpoint
   * Returns new ID token, null if token is invalid (triggers logout), or throws on network errors
   * Prevents concurrent refresh attempts by caching the promise
   *
   * For email/password users: Uses Cognito SDK (refreshSession)
   * For OAuth users (Google/Apple): Falls back to token endpoint (refreshTokensWithEndpoint)
   *
   * Important: Only returns null for RefreshTokenInvalidError (triggers logout).
   * Network errors are thrown to prevent logout when offline.
   */
  const refreshIdToken = useCallback(async (): Promise<string | null> => {
    // Return existing promise if refresh is already in progress
    if (globalRefreshPromise) {
      return globalRefreshPromise;
    }

    // Create new refresh promise
    globalRefreshPromise = (async () => {
      try {
        // Try Cognito SDK first (works for email/password users)
        const newSession = await refreshSession();
        const newIdToken = newSession.getIdToken().getJwtToken();

        // Update token in auth store
        setIdToken(newIdToken);
        return newIdToken;
      } catch (sdkError) {
        // Cognito SDK failed - likely an OAuth user (Google/Apple)
        // Fall back to using the stored refresh token with OAuth endpoint
        if (!refreshToken) {
          logger.error(
            'No refresh token available for fallback',
            sdkError as Error,
            {
              endpoint: 'cognito:refreshSession',
            }
          );
          // No refresh token = user needs to login again
          return null;
        }

        try {
          const tokens = await refreshTokensWithEndpoint(refreshToken);
          const newIdToken = tokens.idToken;

          // Update token in auth store
          setIdToken(newIdToken);
          return newIdToken;
        } catch (endpointError) {
          // Only return null (triggering logout) if token is actually invalid
          // For network errors, throw so user stays logged in but API call fails
          if (endpointError instanceof RefreshTokenInvalidError) {
            logger.error(
              'Refresh token is invalid or expired',
              endpointError as Error,
              {
                endpoint: 'cognito:refreshTokensWithEndpoint',
              }
            );
            return null;
          }

          // Network or other errors - throw to prevent logout
          // User stays logged in, API call will fail with network error
          throw endpointError;
        }
      } finally {
        // Clear cache after refresh completes
        globalRefreshPromise = null;
      }
    })();

    return globalRefreshPromise;
  }, [setIdToken, refreshToken]);

  const apiCall = useCallback(
    async <TResponse>(
      endpoint: string,
      options: ApiOptions = {},
      retryCount = 0,
      overrideToken?: string | null
    ): Promise<ApiResponse<TResponse>> => {
      const {
        skipAuth = false,
        onUnauthorized,
        responseType = 'json',
        timeoutMs = DEFAULT_TIMEOUT_MS,
        ...requestOptions
      } = options;

      const url = `${import.meta.env.VITE_API_URL}/api/v1${endpoint}`;
      const headers: HeadersInit = {
        'X-Platform': 'web',
        'X-App-Version': APP_VERSION,
        ...requestOptions.headers,
      };

      // Always set Content-Type for POST/PUT/PATCH/DELETE requests
      // This ensures HTTP API Gateway doesn't add a default Content-Type
      const method = (requestOptions.method || 'GET').toUpperCase();
      const needsContentType = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(
        method
      );
      if (needsContentType) {
        (headers as Record<string, string>)['Content-Type'] =
          'application/json';
      }

      // Fastify requires a body when Content-Type is application/json
      // Send empty object if no body provided for methods that need Content-Type
      const body = requestOptions.body ?? (needsContentType ? '{}' : undefined);

      // Use override token for retries, otherwise get from store or Cognito
      let currentToken = overrideToken ?? useAuthStore.getState().idToken;

      // If no token in store, try to get fresh token from Cognito
      if (!currentToken && !skipAuth) {
        try {
          const tokens = await getTokens();
          currentToken = tokens?.idToken || null;
          if (currentToken) {
            setIdToken(currentToken);
          }
        } catch {
          // Ignore - will handle auth error below
        }
      }

      if (!skipAuth && currentToken) {
        (headers as Record<string, string>).Authorization =
          `Bearer ${currentToken}`;
      }

      // Shadow mode: inject shadow token header
      const shadowState = useShadowStore.getState();
      if (shadowState.shadowToken) {
        if (shadowState.isExpired()) {
          shadowState.endShadow();
        } else {
          (headers as Record<string, string>)['X-Shadow-Token'] =
            shadowState.shadowToken;
        }
      }

      try {
        // CRITICAL: Don't attempt fetch when offline - preserve cached data
        // Uses enhanced checkIsOnline() which handles VPN scenarios
        if (!checkIsOnline()) {
          const offlineError = new Error(
            'You are offline. Using cached data.'
          ) as ApiError;
          offlineError.code = 'OFFLINE';
          throw offlineError;
        }

        // Add AbortController for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        const response = await fetch(url, {
          ...requestOptions,
          body,
          headers,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        const data =
          responseType === 'blob'
            ? await response.blob()
            : responseType === 'text'
              ? await response.text()
              : await response.json();

        if (!response.ok) {
          const errorCode =
            typeof data === 'object' && data
              ? data.error?.code || data.code
              : undefined;

          // Handle token expiration with auto-refresh
          if (
            response.status === 401 ||
            (errorCode &&
              (errorCode === 'INVALID_TOKEN' ||
                errorCode === 'TOKEN_EXPIRED' ||
                errorCode === 'NO_TOKEN'))
          ) {
            // Try to refresh the token (only once per request)
            if (retryCount === 0 && !skipAuth) {
              let newIdToken: string | null = null;
              let refreshFailed = false;

              try {
                newIdToken = await refreshIdToken();
              } catch {
                // Network error during refresh - don't log out
                // User stays logged in but API call fails
                refreshFailed = true;
              }

              if (newIdToken) {
                // Retry the request with the new token
                return apiCall<TResponse>(
                  endpoint,
                  options,
                  retryCount + 1,
                  newIdToken
                );
              }

              if (refreshFailed) {
                // Network error - don't log out, throw network error
                const networkError = new Error(
                  'Network error during token refresh'
                ) as ApiError;
                networkError.code = 'NETWORK_ERROR';
                throw networkError;
              }

              // newIdToken is null and no network error = token is invalid, log out
            }

            // If refresh failed (invalid token) or not attempted, log out
            handleAuthError(onUnauthorized);

            const error = new Error('Authentication required') as ApiError;
            error.status = 401;
            error.code = errorCode || 'UNAUTHORIZED';
            throw error;
          }

          const error = new Error(
            (typeof data === 'object' && data && data.error?.message) ||
              `HTTP ${response.status}: ${response.statusText}`
          ) as ApiError;
          error.status = response.status;
          error.code =
            (typeof data === 'object' && data && data.error?.code) ||
            data?.code;
          error.details =
            (typeof data === 'object' && data && data.error?.details) ||
            data?.details;
          error.translationKey =
            typeof data === 'object' && data
              ? data.error?.translationKey
              : undefined;

          // Preserve the full response structure for error handling
          (error as ApiError & { response?: unknown }).response = {
            data: data,
            status: response.status,
            statusText: response.statusText,
          };

          throw error;
        }

        return data;
      } catch (error) {
        // Handle abort/timeout errors
        if (error instanceof Error && error.name === 'AbortError') {
          const apiError = new Error(
            'Request timeout. Please try again.'
          ) as ApiError;
          apiError.code = 'TIMEOUT';
          throw apiError;
        }

        // Handle network errors
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          const apiError = new Error(
            'Network error. Please check your connection.'
          ) as ApiError;
          apiError.code = 'NETWORK_ERROR';
          throw apiError;
        }

        // Retry logic for transient errors only
        // Note: 404 is NOT retried - it's a definitive "not found" response
        if (retryCount < 2 && error instanceof Error) {
          const shouldRetry =
            error.message.includes('Network error') ||
            error.message.includes('Failed to fetch');

          if (shouldRetry) {
            await new Promise(resolve =>
              setTimeout(resolve, (retryCount + 1) * 1000)
            );
            return apiCall(endpoint, options, retryCount + 1);
          }
        }

        throw error;
      }
    },
    [refreshIdToken, setIdToken, handleAuthError]
  );

  const get = useCallback(
    <TResponse>(endpoint: string, options?: ApiOptions) =>
      apiCall<TResponse>(endpoint, { ...options, method: 'GET' }),
    [apiCall]
  );

  const post = useCallback(
    <TResponse, TRequest = unknown>(
      endpoint: string,
      body?: TRequest,
      options?: ApiOptions
    ) =>
      apiCall<TResponse>(endpoint, {
        ...options,
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      }),
    [apiCall]
  );

  const put = useCallback(
    <TResponse, TRequest = unknown>(
      endpoint: string,
      body?: TRequest,
      options?: ApiOptions
    ) =>
      apiCall<TResponse>(endpoint, {
        ...options,
        method: 'PUT',
        body: body ? JSON.stringify(body) : undefined,
      }),
    [apiCall]
  );

  const patch = useCallback(
    <TResponse, TRequest = unknown>(
      endpoint: string,
      body?: TRequest,
      options?: ApiOptions
    ) =>
      apiCall<TResponse>(endpoint, {
        ...options,
        method: 'PATCH',
        body: body ? JSON.stringify(body) : undefined,
      }),
    [apiCall]
  );

  const del = useCallback(
    <TResponse, TRequest = unknown>(
      endpoint: string,
      body?: TRequest,
      options?: ApiOptions
    ) =>
      apiCall<TResponse>(endpoint, {
        ...options,
        method: 'DELETE',
        body: body ? JSON.stringify(body) : undefined,
      }),
    [apiCall]
  );

  // The example group is served from here (see lib/exampleGroup.ts) so
  // every page renders it through the same client with no special-casing.
  return useMemo(
    () =>
      withExampleGroup(
        {
          apiCall,
          get,
          post,
          put,
          patch,
          delete: del,
          request: apiCall,
        },
        exampleGroupProvider
      ),
    [apiCall, get, post, put, patch, del]
  );
}
